import { Fragment, useRef, useEffect, useState } from 'react';
import Box from '@prezzee/ribbon-ui/lib/components/Box';
import Container from '@prezzee/ribbon-ui/lib/components/Container';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

// Hooks
import useSegment from 'hooks/useSegment';
import useIsFirstRender from 'hooks/useIsFirstRender';

// Reducers
import { ActionType, CheckoutState, useProductPageReducer } from 'reducers/ProductPageReducer';
import { useCardCustomisationReducer } from 'reducers/CardCustomisationReducer';

// SSR Components
import { ProductSchemaMarkup } from 'components/pages/store/ProductSchemaMarkup';
import { ProductPageContext } from 'contexts/ProductPageContext';
import CardSummary from 'components/pages/store/CardSummary';
import PickSection from 'components/pages/store/PickSection';
import AccordionStep from 'components/pages/store/AccordionStep';
import SeoMeta from 'components/library/SeoMeta';
import CustomCardDesign from 'components/pages/store/CustomCardDesign';
import ZagUpgradeWrapper from './zag/ZagUpgradeWrapper';

// Dynamic Components
const FullCardDetails = dynamic(() => import('components/pages/store/FullCardDetails'), { ssr: false });
const ZagCardDetails = dynamic(() => import('components/pages/store/zag/ZagCardDetails'), { ssr: false });
const PreviewSection = dynamic(() => import('components/pages/store/PreviewSection'), { ssr: false });
const PaymentSummary = dynamic(() => import('components/pages/store/PaymentSummary'), { ssr: false });
const WrapSection = dynamic(() => import('components/pages/store/WrapSection'));
const AlertBox = dynamic(() => import('components/AlertBox'), { ssr: false });
const PromoModal = dynamic(() => import('components/library/PromoModal'), { ssr: false });
const RetailerTnCModal = dynamic(() => import('components/pages/store/RetailerTnCModal'), { ssr: false });
const PaypalCancelledModal = dynamic(() => import('components/pages/store/PaypalCancelledModal'), { ssr: false });
const DeliverSection = dynamic(() => import('components/pages/store/DeliverSection'), { ssr: false });
const SuccessView = dynamic(() => import('components/pages/product/SuccessView.gen').then(mod => mod.make), {
  ssr: false,
});

// Utils
import useWindowSize from 'utils/useWindowSize';
import { generateUniqueUserId } from 'utils/ProductPageSegmentHook.bs';
import { getCurrency, isMultiPurchaseFF, Replacement } from 'common/util';
import { formatValue, getRecipientDetails, getWrapSubtitle } from 'utils/ProductPageHelpers';

// Models
import { GiftTheme } from 'models/gift.model';
import { Sku } from 'models/sku.model';
import { WebConfig } from 'models/webConfig.model';
import { IconTags, SkuPresentation } from 'models/Models';
import { Step, STEP, Steps } from 'models/purchaseForm.model';
import { Nullable } from '../../../models/types';

import useAuth from 'contexts/AuthContext';
import { useLayoutData } from 'contexts/LayoutDataContext';
import ZagBuyingPrompt from './zag/ZagBuyingPrompt';
import { ZagBuyingPrompt as ZagBuyingPromptT } from 'api/queries/zagBuyingPrompt';

export interface ProductPageProps {
  sku: Sku | null;
  configs: WebConfig | null;
  skuPresentation: Nullable<SkuPresentation>;
  giftThemes: Nullable<GiftTheme[]>;
  personalisedMessages: Nullable<string[]>;
  zagBuyingPrompt: Nullable<ZagBuyingPromptT>;
}

const ProductPage = ({
  sku,
  configs,
  skuPresentation,
  personalisedMessages,
  giftThemes,
  zagBuyingPrompt,
}: ProductPageProps): JSX.Element => {
  const [state, dispatch] = useProductPageReducer(configs?.featureStripeBillingAddress || false);
  const isFirstRender = useIsFirstRender();

  const { formValues } = state;
  const { user } = useAuth();
  const [hasUpgradedZag, setHasUpgradedZag] = useState(false);
  const [purchaseInstanceId, _] = useState<string>(generateUniqueUserId());
  const { productViewed, checkoutStepViewed, checkoutStepCompleted, customElementInteraction } = useSegment();

  const currencyCode = sku?.denominations?.[0]?.valueCurrency;
  const { cc, symbol, name } = getCurrency(currencyCode) || {};
  const currency = { cc, symbol, name };
  const { width } = useWindowSize();

  const router = useRouter();
  const preselectedValueQuery = router.query?.value as string;
  const formattedPreselectedValue = formatValue(preselectedValueQuery);
  const preselectedValueExists = sku.denominations.some(
    denomination => denomination.value === formattedPreselectedValue
  );
  const [invalidValuePopupVisible, setInvalidValuePopupVisible] = useState(false);
  const { warnings } = useLayoutData();
  const preselectValueWarnings = (warnings &&
    warnings.find(warning => warning.id === 'card_denomination_not_available')) || {
    heading: 'Oops',
    description: `
      <p>
        The \${0} eGift card is temporarily sold out. We restock frequently so come back in a little
        while or choose another eGift card value.
      </p>
    `,
  };

  const iconTags: IconTags = {
    skuTagTitle: skuPresentation?.skuTagTitle,
    skuIconTags: skuPresentation?.skuIconTags,
  };

  const stepMappings = {
    0: 'choose_step',
    1: 'wrap_step',
    2: 'send_step',
    3: 'preview_step',
    4: 'pay_step',
  };

  const [cardCustomisationState, dispatchCardCustomisationAction] = useCardCustomisationReducer(sku);
  const userUploadedDesignFileInput = useRef(null);

  const handleInvalidValuePopupDismiss = () => setInvalidValuePopupVisible(false);

  const isMultiPurchase = isMultiPurchaseFF(sku.multiPurchase, configs.multiPurchaseToggle, user);

  let isLayer = false;

  if (configs.featureLayerBuyingWeb) {
    if (sku.layerEnabled) {
      if (sku.layerType === 'default') isLayer = true;
      if (sku.layerType === 'upgrade') isLayer = hasUpgradedZag;
    }
  }

  const handleStepToggle = (step: Steps) => () => {
    let newStep;
    const previousStep = stepMappings[state?.step];

    switch (step) {
      case 'Choose':
        newStep = STEP.CHOOSE;
        checkoutStepViewed({
          purchase_instance_id: purchaseInstanceId,
          previous_step: previousStep,
          step: 1,
          step_name: 'choose_step',
        });
        break;
      case 'Wrap':
        newStep = STEP.WRAP;
        break;
      case 'Send':
        newStep = STEP.SEND;
        break;
      case 'Preview':
        newStep = STEP.PREVIEW;
        break;
      case 'Pay':
        newStep = STEP.PAY;
        break;
    }

    dispatch({
      type: ActionType.SetStep,
      payload: newStep,
    });
    dispatch({
      type: ActionType.SetPreviousStep,
      payload: previousStep,
    });
  };

  const pickRef = useRef<HTMLElement>(null);
  const wrapRef = useRef<HTMLElement>(null);
  const sendRef = useRef<HTMLElement>(null);
  const previewRef = useRef<HTMLElement>(null);
  const payRef = useRef<HTMLElement>(null);

  const recipient = getRecipientDetails(state.formValues, user);
  const isMultiRecipient = state.formValues.recipients.length > 1;

  const steps: { [key in Steps]: Step } = {
    Choose: {
      subtitle: `${symbol}${formValues.value.replace(/\.00/g, '')}, for ${
        formValues?.purchaseType === 'self' ? 'myself' : 'someone else'
      }`,
      disabled: state.checkoutState === CheckoutState.Submitting,
      hidden: state.step !== STEP.CHOOSE,
      component: <PickSection preselectedValue={preselectedValueExists ? formattedPreselectedValue : undefined} />,
      ref: pickRef,
      index: STEP.CHOOSE,
    },
    Wrap: {
      subtitle: getWrapSubtitle(formValues),
      disabled:
        state.step < STEP.WRAP ||
        (state.step > STEP.WRAP && formValues.purchaseType === 'self') ||
        state.checkoutState === CheckoutState.Submitting,
      hidden: state.step > STEP.WRAP && formValues.purchaseType === 'friend',
      component: <WrapSection />,
      ref: wrapRef,
      index: STEP.WRAP,
    },
    Send: {
      subtitle: recipient ? `For ${recipient}` : '',
      disabled:
        state.step < STEP.SEND ||
        (state.step > STEP.SEND && !!user && state.formValues?.purchaseType === 'self') ||
        state.checkoutState === CheckoutState.Submitting,
      hidden:
        state.step > STEP.SEND &&
        ((!user && state.formValues?.purchaseType === 'self') || state.formValues?.purchaseType === 'friend'),
      component: <DeliverSection />,
      ref: sendRef,
      index: STEP.SEND,
    },
    Preview: {
      subtitle: undefined,
      hidden: state.step > STEP.PREVIEW,
      disabled: state.step < STEP.PREVIEW || state.checkoutState === CheckoutState.Submitting,
      component: <PreviewSection />,
      ref: previewRef,
      index: STEP.PREVIEW,
    },
    Pay: {
      subtitle: undefined,
      hidden: false,
      disabled: state.step !== STEP.PAY,
      component: <PaymentSummary />,
      ref: payRef,
      index: STEP.PAY,
    },
  };

  const guestSelfSteps: Steps[] = ['Choose', 'Send', 'Preview', 'Pay'];
  const userSelfSteps: Steps[] = ['Choose', 'Preview', 'Pay'];
  const friendSteps: Steps[] = ['Choose', 'Wrap', 'Send', 'Preview', 'Pay'];

  const checkoutSteps =
    state.formValues?.purchaseType === 'self'
      ? user
        ? userSelfSteps
        : guestSelfSteps
      : state.formValues?.purchaseType === 'friend'
      ? friendSteps
      : Object.keys(steps);

  useEffect(() => {
    productViewed(sku, purchaseInstanceId);
    setInvalidValuePopupVisible(preselectedValueQuery && !preselectedValueExists);
  }, []);

  useEffect(() => {
    if (state.step === STEP.SEND && user && state.formValues.purchaseType === 'self') {
      checkoutStepCompleted({
        journey: 'send_now',
        recipient_count: formValues.purchaseType === 'friend' ? formValues?.recipients?.length || 1 : 1,
        purchase_instance_id: purchaseInstanceId,
        step: 3,
        step_name: 'send_step',
      });

      dispatch({ type: ActionType.SetStep, payload: STEP.PREVIEW });
    }
  }, [user]);

  useEffect(() => {
    let currentStepElement: HTMLElement | null = null;

    switch (state.step) {
      case STEP.CHOOSE:
        currentStepElement = pickRef.current;
        break;
      case STEP.WRAP:
        currentStepElement = wrapRef.current;
        break;
      case STEP.SEND:
        currentStepElement = sendRef.current;
        break;
      case STEP.PREVIEW:
        currentStepElement = previewRef.current;
        break;
      case STEP.PAY:
        currentStepElement = payRef.current;
    }

    if (currentStepElement && !isFirstRender) {
      const headerOffset = document.querySelector('header')?.getBoundingClientRect()?.height || 0;

      const newScroll = currentStepElement.getBoundingClientRect().top + window.scrollY - headerOffset;

      window.scrollTo({ top: newScroll, behavior: 'smooth' });
    }
  }, [state.step]);

  //reset promocode on value change
  const valueSelected = state.formValues?.value;

  const resetPromo = () => {
    dispatch({ type: ActionType.SetFormValues, payload: { promo: null } });
  };

  useEffect(() => {
    resetPromo();
  }, [valueSelected]);

  return (
    <>
      {skuPresentation?.seoMeta ? <SeoMeta meta={skuPresentation.seoMeta} /> : null}
      <ProductPageContext.Provider
        value={{
          sku,
          currency,
          state,
          configs,
          dispatch,
          isLayer,
          isMultiPurchase,
          purchaseInstanceId,
          personalisedMessages,
          giftThemes,
          cardCustomisationState,
          dispatchCardCustomisationAction,
          userUploadedDesignFileInput,
          setHasUpgradedZag,
        }}
      >
        <ProductSchemaMarkup sku={sku} />
        {!state.orderResult ? (
          <Fragment>
            {!preselectedValueExists && invalidValuePopupVisible && (
              <AlertBox
                heading={preselectValueWarnings.heading}
                text={preselectValueWarnings.description.replace(Replacement.Zero, formattedPreselectedValue)}
                isVisible={invalidValuePopupVisible}
                dismissHandler={handleInvalidValuePopupDismiss}
                timeout={7000}
              />
            )}
            <PaypalCancelledModal />
            <Container maxW="container.md">
              <Box
                display="flex"
                gap={{ base: '16px', xl: '70px' }}
                justifyContent="center"
                flexDirection={{ base: 'column', lg: 'row' }}
                pb={70}
                px={{ base: '10px', lg: '70px' }}
              >
                <CardSummary iconTags={iconTags} zagBuyingPrompt={zagBuyingPrompt} />
                <Box
                  display="flex"
                  w={{ base: '100%', lg: '50%' }}
                  flexDirection="column"
                  mt={{ base: '16px', lg: '70px' }}
                >
                  {configs.featureLayerBuyingWeb && (
                    <>
                      {sku?.layerEnabled && sku?.layerType === 'upgrade' && <ZagUpgradeWrapper />}
                      {width > 986 && sku?.layerEnabled && sku?.layerType === 'default' && (
                        <ZagBuyingPrompt
                          imageUrl={zagBuyingPrompt?.elements?.image?.value?.[0]?.url}
                          title={zagBuyingPrompt?.elements?.title?.value}
                          description={zagBuyingPrompt?.elements?.description?.value?.replace('{0}', sku.name)}
                          modalLinkText={zagBuyingPrompt?.elements?.zag_explanation_modal_link?.value}
                        />
                      )}
                    </>
                  )}

                  {checkoutSteps.map(key => (
                    <AccordionStep
                      ref={steps[key]?.ref}
                      key={key}
                      title={key}
                      subtitle={steps[key].subtitle}
                      stepIndex={steps[key].index}
                      currentStep={state.step}
                      furthestStep={state.furthestStep}
                      disabled={steps[key].disabled}
                      hidden={steps[key].hidden}
                      onToggle={handleStepToggle(key as Steps)}
                    >
                      {steps[key].component}
                    </AccordionStep>
                  ))}

                  {isLayer ? (
                    <Fragment>
                      <ZagCardDetails />
                      <RetailerTnCModal />
                    </Fragment>
                  ) : (
                    <Fragment>
                      <FullCardDetails />
                      <RetailerTnCModal />
                    </Fragment>
                  )}
                </Box>
              </Box>
            </Container>

            {sku.promos.length > 0 ? sku.promos.map(promo => <PromoModal key={promo.uid} promo={promo} />) : null}

            <CustomCardDesign />
          </Fragment>
        ) : (
          <SuccessView
            // @ts-ignore
            orderNumber={state.orderResult.orderNumber}
            status={state.orderResult.status}
            purchaseType={state.formValues.purchaseType === 'friend' ? 'Friend' : 'Self'}
            recipientName={getRecipientDetails(state.formValues, user)}
            isLoggedin={!!user}
            isMultiRecipient={isMultiRecipient}
            guestEmail={state.formValues.guestEmail}
            guestFirstName={state.formValues.guestFirstName}
            guestLastName={state.formValues.guestLastName}
            isLayer={isLayer}
          />
        )}
      </ProductPageContext.Provider>
    </>
  );
};

export default ProductPage;
